import Dexie, { type Table } from 'dexie';
import type { BootstrapData, Cliente, Moneda, Producto, Tasa, Usuario } from '../api/types';

export interface PedidoLocal {
  id: string;
  serverId?: string;
  numero: string;
  clienteId: string;
  clienteNombre: string;
  clienteRif: string;
  vendedorNombre: string;
  estado: string;
  moneda: Moneda;
  tasaAplicada: number;
  totalUsd: number;
  totalMoneda: number;
  cajas: number;
  createdAt: string;
  observaciones?: string;
  items: Array<{
    productoId: string;
    sku: string;
    nombre: string;
    cantidad: number;
    precioUnitarioUsd: number;
    impuestoPct: number;
  }>;
  syncStatus: 'queued' | 'synced' | 'error';
  syncError?: string;
}

export interface OutboxItem {
  seq?: number;
  mutationId: string;
  orderId: string;
  entity: 'pedido' | 'cobranza' | 'cotizacion';
  idempotencyKey: string;
  payload: Record<string, unknown>;
  createdAt: string;
  status: 'queued' | 'syncing' | 'synced' | 'error';
  attempts: number;
  error?: string;
}

export interface LocalMeta {
  key: string;
  value: unknown;
}

class GravencaDatabase extends Dexie {
  products!: Table<Producto & { updatedAt: string }, string>;
  clients!: Table<Cliente & { updatedAt: string }, string>;
  rates!: Table<Tasa, string>;
  orders!: Table<PedidoLocal, string>;
  outbox!: Table<OutboxItem, number>;
  kv!: Table<LocalMeta, string>;

  constructor() {
    super('gravenca-erp');
    this.version(1).stores({
      products: 'id, sku, nombre',
      clients: 'id, rif, nombre, vendedorId',
      rates: 'moneda',
      orders: 'id, estado, createdAt, syncStatus',
      outbox: '++seq, mutationId, orderId, entity, status, idempotencyKey',
      kv: 'key',
    });
  }
}

export const db = new GravencaDatabase();

export async function saveBootstrap(data: BootstrapData): Promise<void> {
  const updatedAt = new Date().toISOString();
  await db.transaction('rw', [db.products, db.clients, db.rates, db.kv], async () => {
    await db.products.clear();
    await db.clients.clear();
    await db.rates.clear();
    await db.products.bulkPut(data.productos.map((item) => ({ ...item, updatedAt })));
    await db.clients.bulkPut(data.clientes.map((item) => ({ ...item, updatedAt })));
    await db.rates.bulkPut(data.tasas);
    await db.kv.put({
      key: 'bootstrap',
      value: {
        serverTime: data.serverTime,
        usuario: data.usuario,
        empresa: data.empresa,
        sucursales: data.sucursales,
        meta: data.meta,
      },
    });
  });
}

export async function readBootstrap(): Promise<BootstrapData | null> {
  const [products, clients, rates, meta] = await Promise.all([
    db.products.toArray(),
    db.clients.toArray(),
    db.rates.toArray(),
    db.kv.get('bootstrap'),
  ]);
  if (products.length === 0 || clients.length === 0) return null;

  const value = (meta?.value ?? {}) as Pick<
    BootstrapData,
    'serverTime' | 'usuario' | 'empresa' | 'sucursales' | 'meta'
  >;

  return {
    serverTime: value.serverTime ?? new Date().toISOString(),
    usuario:
      value.usuario ??
      ({
        id: 'offline',
        nombre: 'Vendedor en ruta',
        rol: 'ventas',
        vendedorCodigo: null,
      } as BootstrapData['usuario']),
    empresa:
      value.empresa ??
      ({ id: 'offline', nombre: 'GRAVENCA, C.A.', rif: 'J-508362438' } as BootstrapData['empresa']),
    sucursales: value.sucursales ?? [],
    productos: products,
    clientes: clients,
    tasas: rates,
    meta: value.meta ?? null,
  };
}

export async function clearLocalData(): Promise<void> {
  // Vaciar tablas mantiene viva la instancia de Dexie (evita DatabaseClosedError
  // cuando el login demo ocurre antes de la primera consulta).
  await db.transaction('rw', db.tables, async () => {
    await Promise.all(db.tables.map((table) => table.clear()));
  });
}

/**
 * Perfil mínimo para reabrir la app sin red. Nunca guarda tokens: el JWT sigue
 * viviendo solo en memoria y el refresh token en su cookie HttpOnly.
 */
export async function saveSessionProfile(user: Usuario): Promise<void> {
  await db.kv.put({
    key: 'session-profile',
    value: { ...user, savedAt: new Date().toISOString() },
  });
}

export async function readSessionProfile(): Promise<Usuario | null> {
  const row = await db.kv.get('session-profile');
  const value = row?.value as (Usuario & { savedAt?: string }) | undefined;
  if (!value?.id || !value?.empresaId) return null;
  const { savedAt: _savedAt, ...user } = value;
  return user;
}

export async function clearSessionProfile(): Promise<void> {
  await db.kv.delete('session-profile');
}
